import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import MainLayout from '../../layout/main-layout';
import { APP_API_URL, APP_NAME, APP_URL } from '../../utils';

const CONTRACTS = {
  cdi: 'CDI',
  cdd: 'CDD',
  essai: 'Période d\'essai',
  stage: 'Stage',
  journalier: 'Journalier',
  prestation: 'Prestation',
};

const UNDER_20 = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize'];

const underHundred = (value) => {
  if (value < 17) return UNDER_20[value];
  if (value < 20) return `dix-${UNDER_20[value - 10]}`;
  if (value < 70) {
    const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante'];
    const ten = Math.floor(value / 10);
    const unit = value % 10;
    if (unit === 0) return tens[ten];
    if (unit === 1) return `${tens[ten]}-et-un`;
    return `${tens[ten]}-${underHundred(unit)}`;
  }
  if (value < 80) {
    if (value === 71) return 'soixante-et-onze';
    return `soixante-${underHundred(value - 60)}`;
  }
  const unit = value - 80;
  if (unit === 0) return 'quatre-vingts';
  return `quatre-vingt-${underHundred(unit)}`;
};

const underThousand = (value) => {
  if (value < 100) return underHundred(value);
  const hundreds = Math.floor(value / 100);
  const rest = value % 100;
  const prefix = hundreds === 1 ? 'cent' : `${underHundred(hundreds)} cent`;
  if (rest === 0) return hundreds > 1 ? `${prefix}s` : prefix;
  return `${prefix} ${underHundred(rest)}`;
};

const montantEnLettres = (value) => {
  const amount = Math.round(Math.abs(Number(value) || 0));
  if (amount === 0) return 'zéro';
  const millions = Math.floor(amount / 1000000);
  const thousands = Math.floor((amount % 1000000) / 1000);
  const rest = amount % 1000;
  const parts = [];
  if (millions) parts.push(millions === 1 ? 'un million' : `${underThousand(millions)} millions`);
  if (thousands) parts.push(thousands === 1 ? 'mille' : `${underThousand(thousands)} mille`);
  if (rest) parts.push(underThousand(rest));
  return parts.join(' ');
};

const periodText = (period, presence) => {
  const label = monthLabel(period);
  if (presence?.unpaid) return `${label} — salaire non versé`;
  const fault = Number(presence?.fault_days) || 0;
  if (fault > 0) {
    const paid = Number(presence?.paid_days) || 0;
    return `${label} — ${paid} jour${paid > 1 ? 's' : ''} payé${paid > 1 ? 's' : ''}, ${fault} jour${fault > 1 ? 's' : ''} de faute`;
  }
  if (!presence || presence.full_month) return label;
  if (!presence.days) return `${label} — aucun jour à payer`;
  const from = formatDay(presence.from);
  const to = formatDay(presence.to);
  const daysLabel = `${presence.days} jour${presence.days > 1 ? 's' : ''} sur ${presence.base_days || 30}`;
  return `${label} — du ${from} au ${to} (${daysLabel})`;
};

const fileUrl = (path, version) => {
  if (!path) return '';
  const stamp = version ? `?v=${encodeURIComponent(version)}` : '';
  return `${APP_URL}/${path}${stamp}`;
};

const money = (value) =>
  `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(Number(value) || 0))} F`;

const familyLabel = {
  celibataire: 'Célibataire',
  marie: 'Marié(e)',
  divorce: 'Divorcé(e)',
  veuf: 'Veuf / veuve',
};

const monthLabel = (period) => {
  const [year, month] = String(period || '').split('-');
  if (!year || !month) return period;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

const formatDay = (value) => {
  if (!value) return '—';
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString('fr-FR');
};

const PAYSLIP_CSS = `
  .payslip {
    max-width: 920px;
    margin: 0 auto;
    background: #fff;
    border: 1px solid #e2e8e0;
    border-radius: 16px;
    box-shadow: 0 8px 28px rgba(0, 102, 51, 0.08);
    overflow: hidden;
    color: #1f2933;
  }
  .payslip__header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1.25rem;
    padding: 1.35rem 1.5rem 1rem;
    background: linear-gradient(180deg, #f8fff4 0%, #fff 100%);
  }
  .payslip__brand { display: flex; gap: 0.85rem; align-items: flex-start; }
  .payslip__brand img { width: 72px; height: 72px; object-fit: contain; }
  .payslip__brand p { margin: 0; font-size: 0.78rem; line-height: 1.45; color: #4a4a4a; }
  .payslip__brand-name { font-size: 1rem !important; font-weight: 800; color: #006633 !important; letter-spacing: 0.04em; }
  .payslip__doc-side { display: flex; align-items: center; gap: 1rem; }
  .payslip__doc { text-align: right; }
  .payslip__doc-title { margin: 0 0 0.25rem; font-size: 1.05rem; font-weight: 800; letter-spacing: 0.04em; color: #006633; text-transform: uppercase; }
  .payslip__doc p { margin: 0; font-size: 0.85rem; color: #555; }
  .payslip__qr { display: flex; flex-direction: column; align-items: center; gap: 0.2rem; background: #fff; padding: 0.25rem; }
  .payslip__qr strong { font-size: 0.72rem; letter-spacing: 0.04em; color: #006633; }
  .payslip__accent { height: 4px; background: linear-gradient(90deg, #006633 0%, #58cc02 55%, #006633 100%); }
  .payslip__body { padding: 1.15rem 1.5rem 1.25rem; }
  .payslip__identity {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.65rem 1rem;
    margin-bottom: 1rem;
    padding: 0.85rem 1rem;
    border: 1px solid #e8efe4;
    border-radius: 12px;
    background: #fafcfa;
  }
  .payslip__identity span { display: block; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #006633; }
  .payslip__identity strong { font-size: 0.88rem; font-weight: 700; }
  .payslip__table { width: 100%; border-collapse: collapse; font-size: 0.86rem; border: 1px solid #1f2933; }
  .payslip__table th {
    background: #006633;
    color: #fff;
    font-weight: 700;
    text-align: left;
    padding: 0.45rem 0.6rem;
    border: 1px solid #1f2933;
  }
  .payslip__table th.num, .payslip__table td.num { text-align: right; white-space: nowrap; }
  .payslip__table td { padding: 0.38rem 0.6rem; border: 1px solid #1f2933; vertical-align: top; }
  .payslip__table td.strong { font-weight: 800; background: #f4fbf0; }
  .payslip__net {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.75rem;
    padding: 0.75rem 0.9rem;
    border-radius: 12px;
    background: #006633;
    color: #fff;
    font-size: 1.05rem;
    font-weight: 800;
  }
  .payslip__words {
    margin: 0.65rem 0 0;
    padding: 0.55rem 0.7rem;
    border: 1px solid #1f2933;
    font-size: 0.84rem;
    line-height: 1.45;
  }
  .payslip__section { margin-top: 1rem; border: 1px solid #1f2933; border-radius: 12px; overflow: hidden; }
  .payslip__section h2 {
    margin: 0;
    padding: 0.45rem 0.75rem;
    background: linear-gradient(135deg, #006633 0%, #3aaa35 70%, #58cc02 100%);
    color: #fff;
    font-size: 0.82rem;
    font-weight: 700;
  }
  .payslip__signs { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; margin-top: 1.5rem; }
  .payslip__signs div { min-height: 4.5rem; border-top: 1px solid #1f2933; padding-top: 0.4rem; font-size: 0.82rem; font-weight: 700; }
  .payslip__sign-images { display: flex; gap: 0.75rem; align-items: flex-end; min-height: 90px; margin-top: 0.35rem; }
  .payslip__sign-images img { object-fit: contain; background: transparent; }
  .payslip__sign-images img.cachet { width: 90px; height: 90px; }
  .payslip__sign-images img.signature { width: 160px; height: 54px; }
  .payslip__signs span { display: block; margin-top: 0.45rem; font-weight: 700; color: #1f2933; font-size: 0.82rem; }
  .payslip__signs em { display: block; margin-top: 2.2rem; font-style: normal; font-weight: 500; color: #6b7280; font-size: 0.75rem; }
  .payslip__legal { margin: 1rem 0 0; font-size: 0.72rem; line-height: 1.45; color: #6b7280; }
  @media (max-width: 720px) {
    .payslip__identity { grid-template-columns: 1fr 1fr; }
    .payslip__doc { text-align: left; }
  }
  @media print {
    @page { size: A4; margin: 10mm; }
    #sidebar, #header, .no-print, .toggle-sidebar-btn { display: none !important; }
    #main, .duo-page, .duo-main-bg { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    .payslip, .payslip * { visibility: visible !important; }
    .payslip {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      max-width: none;
      border: none;
      border-radius: 0;
      box-shadow: none;
    }
    .payslip, .payslip__header, .payslip__accent, .payslip__table th, .payslip__net, .payslip__section h2 {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

export default function BulletinPaie() {
  const { personnelId } = useParams();
  const [searchParams] = useSearchParams();
  const period = searchParams.get('mois') || new Date().toISOString().slice(0, 7);
  const shouldPrint = searchParams.get('print') === '1';
  const printed = useRef(false);
  const [payload, setPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${APP_API_URL}/payslips/preview`,
        { personnel_id: personnelId, period },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayload(response.data);
    } catch (err) {
      setError('Impossible de calculer le bulletin.');
    } finally {
      setIsLoading(false);
    }
  }, [personnelId, period]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!shouldPrint || printed.current || isLoading || !payload?.bulletin) return undefined;
    printed.current = true;
    const timer = window.setTimeout(() => window.print(), 500);
    return () => window.clearTimeout(timer);
  }, [shouldPrint, isLoading, payload]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${APP_API_URL}/payslips`,
        { personnel_id: personnelId, period },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!payload?.payslip_id) return;
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${APP_API_URL}/payslips/${payload.payslip_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch (err) {
      setError('Suppression impossible.');
    } finally {
      setSaving(false);
    }
  };

  const person = payload?.personnel;
  const bulletin = payload?.bulletin;
  const visibleGains = (bulletin?.gains || []).filter((line) => Number(line.amount) !== 0 || line.code === 'base');
  const visibleDeductions = (bulletin?.retenues || []).filter((line) => Number(line.amount) !== 0);
  const totalRetenues = visibleDeductions.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const gainLines = [...visibleGains, { code: 'brut', label: 'Salaire brut', amount: bulletin?.brut, strong: true }];
  const deductionLines = [...visibleDeductions, { code: 'total-retenues', label: 'Total des retenues', amount: totalRetenues, strong: true }];
  const rowCount = Math.max(gainLines.length, deductionLines.length);
  const childrenCount = person?.nombre_enfants ?? bulletin?.nombre_enfants ?? 0;
  const editedOn = new Date().toLocaleDateString('fr-FR');
  const matricule = person?.matricule || '—';
  const presence = bulletin?.presence || null;
  const employer = payload?.employer;
  const signatureUrl = fileUrl(employer?.signature_path, employer?.updated_at);
  const cachetUrl = fileUrl(employer?.cachet_path, employer?.updated_at);
  const totalCharges = (bulletin?.charges_patronales || []).reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const stalePartial = Boolean(payload?.saved && payload?.presence && !payload.presence.full_month && !bulletin?.presence);
  const qrValue = [
    'ASEBAT',
    `Matricule: ${matricule}`,
    `Salarié: ${person?.name || ''}`,
    `Période: ${period}`,
    `Net: ${Math.round(Number(bulletin?.net) || 0)} FCFA`,
  ].join('\n');

  return (
    <MainLayout>
      <style>{PAYSLIP_CSS}</style>
      <div className="duo-page">
        <div className="d-flex flex-wrap gap-2 mb-3 no-print">
          <Link to={`/personnel/paie?mois=${period}`} className="duo-btn duo-btn--ghost">Paie du mois</Link>
          <Link to={`/personnel/${personnelId}/dossier`} className="duo-btn duo-btn--ghost">Dossier</Link>
          <Link to="/personnel/paie/reglages" className="duo-btn duo-btn--ghost">Signature et cachet</Link>
          <button type="button" className="duo-btn duo-btn--primary" onClick={save} disabled={saving || isLoading}>
            {payload?.saved ? 'Mettre à jour le bulletin' : 'Enregistrer le bulletin'}
          </button>
          <button type="button" className="duo-btn duo-btn--ghost" onClick={() => window.print()} disabled={!bulletin}>
            Imprimer
          </button>
          {payload?.saved && (
            <button type="button" className="duo-btn duo-btn--ghost" onClick={remove} disabled={saving}>
              Retirer
            </button>
          )}
        </div>

        {error && <p className="text-danger no-print">{error}</p>}
        {isLoading && <p>Calcul du bulletin…</p>}

        {bulletin && (
          <article className="payslip">
            <header className="payslip__header">
              <div className="payslip__brand">
                <img src="/assets/img/logo_v2.png" alt={APP_NAME} />
                <div>
                  <p className="payslip__brand-name">ASEBAT</p>
                  <p>C/1775 C, QUARTIER GBEDJEKOME - OUEDO</p>
                  <p>IFU 3202434985932</p>
                  <p>info@asebat.com · +229 01 61 89 89 00</p>
                </div>
              </div>
              <div className="payslip__doc-side">
                <div className="payslip__doc">
                  <p className="payslip__doc-title">Bulletin de paie</p>
                  <p>Période : <strong className="text-capitalize">{periodText(period, presence)}</strong></p>
                  <p>Matricule : <strong>{matricule}</strong></p>
                  <p>Édité le : {editedOn}</p>
                </div>
                <div className="payslip__qr">
                  <QRCodeSVG value={qrValue} size={88} level="M" includeMargin />
                  <strong>{matricule}</strong>
                </div>
              </div>
            </header>
            <div className="payslip__accent" />
            <div className="payslip__body">
              <div className="payslip__identity">
                <p><span>Matricule</span><strong>{matricule}</strong></p>
                <p><span>Salarié</span><strong>{person?.name || '—'}</strong></p>
                <p><span>Fonction</span><strong>{person?.fonction || person?.poste || '—'}</strong></p>
                <p><span>Type de contrat</span><strong>{CONTRACTS[person?.type_contrat] || '—'}</strong></p>
                <p><span>N° CNSS</span><strong>{person?.numero_cnss || '—'}</strong></p>
                <p><span>N° IFU</span><strong>{person?.numero_ifu || '—'}</strong></p>
                <p><span>Embauche</span><strong>{formatDay(person?.date_embauche)}</strong></p>
                <p><span>Situation matrimoniale</span><strong>{familyLabel[person?.situation_matrimoniale] || 'Non renseignée'}</strong></p>
                <p><span>Enfants</span><strong>{childrenCount}</strong></p>
              </div>

              {(bulletin.alerts || []).map((alert) => (
                <p key={alert} className="text-danger">{alert}</p>
              ))}

              <table className="payslip__table">
                <thead>
                  <tr>
                    <th>Gains</th>
                    <th className="num">Montant</th>
                    <th>Retenues</th>
                    <th className="num">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: rowCount }, (_, index) => {
                    const gain = gainLines[index];
                    const deduction = deductionLines[index];
                    return (
                      <tr key={gain?.code || deduction?.code || index}>
                        <td className={gain?.strong ? 'strong' : undefined}>{gain?.label || ''}</td>
                        <td className={gain?.strong ? 'num strong' : 'num'}>{gain ? money(gain.amount) : ''}</td>
                        <td className={deduction?.strong ? 'strong' : undefined}>{deduction?.label || ''}</td>
                        <td className={deduction?.strong ? 'num strong' : 'num'}>{deduction ? money(deduction.amount) : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="payslip__net">
                <span>Net à payer</span>
                <span>{money(bulletin.net)}</span>
              </div>
              <p className="payslip__words">
                Arrêté le présent bulletin à la somme de <strong>{montantEnLettres(bulletin.net)} francs CFA</strong>.
              </p>
              {stalePartial && (
                <p className="text-danger no-print">
                  Ce bulletin enregistré est encore un mois complet. {payload.presence.days} jours sont dus. Cliquez sur Mettre à jour le bulletin pour appliquer le prorata.
                </p>
              )}

              <section className="payslip__section">
                <h2>Charges patronales</h2>
                <table className="payslip__table">
                  <tbody>
                    {(bulletin.charges_patronales || []).map((line) => (
                      <tr key={line.code}>
                        <td>{line.label}</td>
                        <td className="num">{money(line.amount)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="strong">Total charges patronales</td>
                      <td className="num strong">{money(bulletin.total_charges_patronales ?? totalCharges)}</td>
                    </tr>
                    <tr>
                      <td className="strong">Coût total employeur</td>
                      <td className="num strong">{money(bulletin.cout_employeur)}</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <div className="payslip__signs">
                <div>
                  Signature et cachet de l&apos;employeur
                  <div className="payslip__sign-images">
                    {cachetUrl ? <img className="cachet" src={cachetUrl} alt="Cachet de l'employeur" /> : null}
                    {signatureUrl ? <img className="signature" src={signatureUrl} alt="Signature de l'employeur" /> : null}
                  </div>
                  <span>{employer?.signataire_nom || ' '}</span>
                </div>
                <div>
                  Le salarié
                  <em>Signature</em>
                </div>
              </div>

              <p className="payslip__legal">
                SMIG 52 000 FCFA (décret n° 2022-692). CNSS : pension 3,6 % salarié et 6,4 % employeur, prestations familiales 9 %,
                risques professionnels selon la classe. Versement patronal sur salaires 4 % (CGI art. 193).
                ITS sur le brut, barème mensuel du CGI art. 125. Prime d&apos;ancienneté : convention collective générale, art. 65.
                Redevance ORTB : 1 000 F en mars et 3 000 F en juin. Les enfants sont indiqués pour le dossier CNSS et ne réduisent plus l&apos;ITS depuis 2022.
              </p>
              {!payload?.saved && (
                <p className="payslip__legal no-print">Aperçu non encore enregistré pour ce mois.</p>
              )}
            </div>
          </article>
        )}
      </div>
    </MainLayout>
  );
}
